<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import { format } from 'date-fns-tz'

import { ImageSrcSetFormat } from '@xcel-next/rendering-tools/types/constants'

import { useTeaser } from '../../../composables/use-teaser'
import { useImgSrcSet } from '../../../composables/use-img-src-set'
import { useLazyLoadingUtils } from '../../../composables/use-lazy-loading-utils'
import TrackingPixel from '../../../components/tracking/TrackingPixel.vue'
import TeaserPicture from '../../../components/teaser/TeaserPicture.vue'
import TeaserTopBadges from '../../../components/teaser/TeaserTopBadges.vue'
import TeaserMediaIcons from '../../../components/teaser/TeaserMediaIcons.vue'
import TeaserSliderControls from '../../../components/teaser/TeaserSliderControls.vue'
import { useEpgAirTimeHelper } from '../../../composables/use-epg-air-time-helper'

import type { TeaserProps } from '../../../composables/use-teaser'
import type { ComposedVodEpgTeaserGroupItem } from '@xcel-next/composer-schema'

type Props = Omit<TeaserProps, 'composedTeaser'> & {
  composedTeaser?: TeaserProps['composedTeaser'] & ComposedVodEpgTeaserGroupItem
}

const props = withDefaults(defineProps<Props>(), {
  composedTeaser: () => ({}),
  metaData: () => ({}),
  includeDepth: 0,
  listOrderPosition: 0,
  isInlineTeaser: false,
  dataSliderInitialSlide: false,
  prioritizeImage: false
})

const {
  fallbackPlaceholderSource,
  showInlineTeaserSliderStyle,
  setBrandBadgeAlt,
  setLinkTarget,
  showDownloadIcon,
  showPlayIcon,
  showGalleryIcon,
  showAdBadge,
  showOwnerBrand,
  imageIsAiGenerated,
  setStyleClasses,
  headline,
  imageAltText,
  copytext,
  imageUrl,
  imageFetchPriority,
  headlineTagType,
  shouldAddDataNoSnippet,
  cdnImageUrl,
  getImageOverlayStyleClass,
  imgSrcSetArray,
  brandBadgeSrc,
  addTeaserImagePreloadLinksToHead
} = useTeaser(props)

const { createImgSrc, getImageAltText } = useImgSrcSet()
const { getLoadingValue } = useLazyLoadingUtils(props)

useHead(computed(() => addTeaserImagePreloadLinksToHead()))

const { forceDateIntoTimeZone, displayAirDate } = useEpgAirTimeHelper()

// whether there's an airtime window to show at all - actual text split into epgBroadcastTimeRange/
// epgBroadcastDate below (separate elements) so the date can wrap onto its own line on narrow cards
const epgBroadcastAirTime = computed(() => {
  return !!(props.composedTeaser.airTime && props.composedTeaser.airTimeEnd)
})

// format matches EpgSearch/Broadcast.vue's timeRangeLabel for the new topic-page cards
// (metaData.isEpgCard, set by EpgSearch/BroadcastCard.vue) - the pre-existing article-slider teasers
// keep their original format
const epgBroadcastTimeRange = computed(() => {
  const { airTime, airTimeEnd } = props.composedTeaser
  if (!airTime || !airTimeEnd) return ''
  const dateStart = forceDateIntoTimeZone(new Date(airTime))
  const dateEnd = forceDateIntoTimeZone(new Date(airTimeEnd))
  return format(dateStart, 'HH:mm') + ' - ' + format(dateEnd, 'HH:mm')
})

const epgBroadcastDate = computed(() => {
  if (!props.composedTeaser.airTime || !props.metaData.isEpgCard) return null
  return displayAirDate(props.composedTeaser.airTime, 'EEE dd.MM.')
})

const channelLogoUrl = (image: NonNullable<ComposedVodEpgTeaserGroupItem['channelLogo']>) => createImgSrc({
  image,
  brand: props.metaData.brand ?? '',
  setName: 'channelLogo',
  type: 'webp',
  srcSetDefinition: {
    format: ImageSrcSetFormat.Square
  }
}) ?? undefined
</script>

<template>
  <section :class="setStyleClasses">
    <div v-if="composedTeaser.externalCssClass"
         :class="composedTeaser.externalCssClass" />
    <a v-else
       :href="composedTeaser.url"
       :rel="composedTeaser.rel"
       :target="setLinkTarget"
       data-tc="teaser">
      <figure v-if="imageUrl"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <TeaserPicture :src-set-array="imgSrcSetArray('webp')"
                       :fallback-placeholder-source="fallbackPlaceholderSource"
                       :loading="getLoadingValue"
                       :fetch-priority="imageFetchPriority"
                       :alt-text="getImageAltText(composedTeaser, imageAltText)" />
        <TeaserTopBadges :show-owner-brand="showOwnerBrand"
                         :show-ad-badge="showAdBadge"
                         :image-is-ai-generated="imageIsAiGenerated"
                         :is-affiliate="composedTeaser?.affiliate"
                         :brand-badge-alt="setBrandBadgeAlt"
                         :brand-badge-src="brandBadgeSrc" />
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon"
             class="bx-badges bx-badges--bottom bx-badges--over-image">
          <TeaserMediaIcons :show-play-icon="showPlayIcon"
                            :show-download-icon="showDownloadIcon"
                            :show-gallery-icon="showGalleryIcon" />
        </div>

        <div v-if="composedTeaser?.streamLogo?.url"
             class="bx-badges bx-badges--top bx-badges--over-image bx-badges--stream-logo">
          <img :src="composedTeaser.streamLogo.url"
               :loading="getLoadingValue"
               :alt="`${composedTeaser.streamLogo.imageAltText || 'Anbieter' } Logo`">
        </div>

        <div v-if="composedTeaser?.channelLogo?.imageId"
             class="bx-badges bx-badges--top bx-badges--over-image bx-badges--channel-logo">
          <img :src="channelLogoUrl(composedTeaser.channelLogo)"
               :loading="getLoadingValue"
               :alt="`${composedTeaser.channelLogo.imageAltText || 'Sender'} Logo`">
        </div>

        <template v-if="showInlineTeaserSliderStyle">
          <TeaserSliderControls :num-slides="composedTeaser.numSlides"
                                :slider-contents="composedTeaser.sliderContents" />
        </template>

      </figure>
      <figure v-else-if="metaData.isSliderPreview"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <img v-if="metaData.isLoading"
             :src="metaData.imgUrlLoading">
        <img v-else-if="metaData.error"
             :src="metaData.imgUrlError">
        <img v-else-if="metaData.errorNotEnoughContent"
             :src="metaData.imgUrlErrorNotEnoughContent">
        <img v-else
             :src="cdnImageUrl('/images/default-placeholder.jpg')">
      </figure>

      <div class="bx-teaser__content"
           data-tc="teaser-content">
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon"
             class="bx-badges bx-badges--bottom bx-badges--over-content">
          <TeaserMediaIcons :show-play-icon="showPlayIcon"
                            :show-download-icon="showDownloadIcon"
                            :show-gallery-icon="showGalleryIcon"
                            variant="content" />
        </div>
        <div class="bx-badges bx-badges--top bx-badges--over-content">
          <span v-if="showAdBadge"
                class="bx-badge bx-badge--ad"
                :class="{ 'bx-badge--affiliate-link': composedTeaser?.affiliate }" />
        </div>

        <div v-if="composedTeaser.roofline"
             class="bx-teaser__roofline"
             :data-nosnippet="shouldAddDataNoSnippet"
             data-tc="teaser-roofline">
          {{ composedTeaser.roofline }}
        </div>

        <component :is="headlineTagType"
                   v-if="headline"
                   :data-nosnippet="shouldAddDataNoSnippet"
                   class="bx-teaser__headline"
                   data-tc="teaser-headline">
          {{ headline }}
        </component>

        <p v-if="copytext"
           :data-nosnippet="shouldAddDataNoSnippet"
           class="bx-teaser__copy">
          {{ copytext }}
        </p>

        <div v-if="showPlayIcon && !epgBroadcastAirTime"
             class="bx-badges bx-badges--below-teaser-content">
          <svg class="bx-badge bx-badge--video icon icon-play">
            <use href="#play" />
          </svg>
        </div>
        <div v-else-if="epgBroadcastAirTime"
             :data-nosnippet="shouldAddDataNoSnippet"
             class="bx-badges bx-badges--below-teaser-content bx-badges--epg-airtime">
          <span v-if="epgBroadcastDate"
                class="bx-badges--epg-airtime__date">{{ epgBroadcastDate }}</span>
          <span class="bx-badges--epg-airtime__time">{{ epgBroadcastTimeRange }}</span>
        </div>
      </div>
      <TrackingPixel v-if="composedTeaser.trackingPixel"
                     :tracking-pixel="composedTeaser.trackingPixel" />
    </a>
  </section>
</template>
